/*
  Lunqet: the offer cards inside the cart drawer (snippets/lq-cart-offers.liquid).
  A tap anywhere on a card includes or removes the offer. The request asks Shopify to re-render the
  cart drawer section, and Horizon's own cart event morphs it in, so lines, card states and totals
  always match the real cart. The three-for-two card carries a per-visitor deadline that never restarts.
*/
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/* Horizon declares Theme with const, so it is a global binding but not a property of window. */
const routes = (typeof Theme !== 'undefined' && Theme.routes) || {
  cart_add_url: '/cart/add.js',
  cart_change_url: '/cart/change',
  cart_update_url: '/cart/update',
  cart_url: '/cart',
};
/* One deadline for both paperback deals. Renamed from lq_cart_bogo_end when the time changed to 53:43, so every visitor starts fresh once. */
const BOGO_END = 'lq_cart_deal_end';
const store = {
  get(k) { try { return localStorage.getItem(k); } catch (e) { return null; } },
  set(k, v) { try { localStorage.setItem(k, v); } catch (e) {} },
};
const json = (url, body) => fetch(url, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  body: JSON.stringify(body),
}).then(async (res) => {
  const data = await res.json();
  if (!res.ok || data.status) throw new Error(data.description || data.message || 'cart');
  return data;
});
const getCart = () => fetch(routes.cart_url + '.js', { cache: 'no-store' }).then((r) => r.json());
const sectionIds = () => [...document.querySelectorAll('cart-items-component')]
  .map((el) => el.dataset.sectionId).filter(Boolean).join(',');

async function toggle(card) {
  if (card.getAttribute('aria-busy') === 'true') return;
  const on = card.getAttribute('aria-pressed') === 'true';
  const kind = card.dataset.lqCo;
  const key = card.dataset.key;
  const sections = sectionIds();

  card.setAttribute('aria-busy', 'true');
  card.classList.toggle('is-on', !on);
  card.setAttribute('aria-pressed', String(!on));

  let action;
  let lines;
  let request;
  if (kind === 'upsell' && !on) {
    action = 'add';
    lines = [{ merchandiseId: card.dataset.variant, quantity: 1 }];
    request = () => json(routes.cart_add_url, { items: [{ id: Number(card.dataset.variant), quantity: 1 }], sections });
  } else if (kind === 'upsell') {
    action = 'remove';
    lines = [{ id: key, quantity: 0 }];
    request = () => json(routes.cart_change_url + '.js', { id: key, quantity: 0, sections });
  } else {
    /* Three for two, or five for three. Taking one sets the paperback to that many and replaces any digital
       edition of the same book (the paperback edition includes the eBook and audiobook). What the cart held
       before the first deal is kept in a hidden cart attribute, so removing a deal puts it back exactly.
       Taking five while three is on upgrades it: the earlier snapshot is kept and the codes swap. */
    const code = card.dataset.code;
    const tier = parseInt(card.dataset.tier, 10) || 3;
    const dealCodes = (card.dataset.codes || code).split(',').map((c) => c.trim().toUpperCase()).filter(Boolean);
    const paperProduct = card.dataset.product;
    const books = (card.dataset.books || '').split(',');
    /* Each book's deal keeps its own snapshot (The Marionette Principle keeps the original name). */
    const fromKey = card.dataset.from || '_lq_bogo_from';
    action = 'update';
    lines = [{ id: key || card.dataset.variant, quantity: on ? 1 : tier }];
    request = async () => {
      const current = await getCart();
      const items = current.items || [];
      const others = (current.discount_codes || []).map((d) => d.code).filter((c) => c && !dealCodes.includes(c.toUpperCase()));
      const paperLines = items.filter((i) => String(i.product_id) === paperProduct);
      const updates = {};
      let attributes;
      if (!on) {
        const digital = items.filter((i) => books.includes(String(i.product_id)) && String(i.product_id) !== paperProduct);
        const kept = (current.attributes || {})[fromKey];
        const inDeal = (current.discount_codes || []).some((d) => d.code && dealCodes.includes(d.code.toUpperCase()));
        const before = {
          paper: paperLines.reduce((n, i) => n + i.quantity, 0),
          lines: digital.map((i) => ({ id: i.variant_id, q: i.quantity })),
        };
        digital.forEach((i) => { updates[i.key] = 0; });
        if (paperLines.length) {
          paperLines.forEach((i, n) => { updates[i.key] = n === 0 ? tier : 0; });
        } else {
          updates[card.dataset.variant] = tier;
        }
        attributes = { [fromKey]: inDeal && kept ? kept : JSON.stringify(before) };
      } else {
        let before = null;
        try { before = JSON.parse((current.attributes || {})[fromKey] || 'null'); } catch (e) { before = null; }
        if (!before) before = { paper: 1, lines: [] };
        paperLines.forEach((i, n) => { updates[i.key] = n === 0 ? before.paper : 0; });
        (before.lines || []).forEach((l) => { updates[l.id] = l.q; });
        attributes = { [fromKey]: '' };
      }
      const codes = on ? others : [...others, code];
      return json(routes.cart_update_url + '.js', { updates, attributes, discount: codes.join(','), sections });
    };
  }

  const deferred = CartLinesUpdateEvent.createPromise();
  card.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'cart', lines, promise: deferred.promise }));
  try {
    const result = await request();
    const cart = result.items && result.token ? result : await getCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'lq-cart', sections: result.sections, didError: false },
    });
  } catch (error) {
    card.classList.toggle('is-on', on);
    card.setAttribute('aria-pressed', String(on));
    deferred.resolve({ detail: { didError: true } });
  } finally {
    card.removeAttribute('aria-busy');
  }
}

document.addEventListener('click', (event) => {
  const card = event.target.closest && event.target.closest('[data-lq-co]');
  if (!card) return;
  event.preventDefault();
  toggle(card);
});

/* Three for two needs three paperbacks, five for three needs five. If the cart drops below three by any other route (the quantity
   buttons, removing a line), the code comes off the cart, so it can never sit on fewer than three. */
/* One entry per book with a paperback deal, keyed by the paperback's product id. */
let bogoInfo = null;
const readBogo = () => {
  /* Remembered across re-renders: the three card disappears while five is on, but its rule still applies. */
  document.querySelectorAll('[data-lq-co="bogo"]').forEach((card) => {
    if (!bogoInfo) bogoInfo = {};
    const p = card.dataset.product;
    if (!bogoInfo[p]) bogoInfo[p] = { product: p, from: card.dataset.from || '_lq_bogo_from', tiers: {} };
    if (card.dataset.code) bogoInfo[p].tiers[card.dataset.code.toUpperCase()] = parseInt(card.dataset.tier, 10) || 3;
  });
  return bogoInfo;
};
let bogoChecking = false;
/* A code tied to the companion (MARIONETTE10 needs The Reply Book) comes off the same way when the companion leaves. */
let needsInfo = null;
const readNeeds = () => {
  const el = document.querySelector('[data-lq-code-needs]');
  if (el) needsInfo = { code: el.dataset.lqCodeNeeds, product: el.dataset.product };
  return needsInfo;
};
async function enforceBogo() {
  const info = readBogo();
  const needs = readNeeds();
  if (!info && (!needs || !needs.code)) return;
  if (bogoChecking) return;
  bogoChecking = true;
  try {
    const cart = await getCart();
    const codes = (cart.discount_codes || []).map((d) => d.code).filter(Boolean);
    const same = (a, b) => a.toUpperCase() === b.toUpperCase();
    const count = (id) => (cart.items || []).filter((i) => String(i.product_id) === id).reduce((n, i) => n + i.quantity, 0);
    const drop = [];
    const add = [];
    const clear = {};
    /* Paperback deals: the cart always carries the best deal its paperback count earns, or none.
       Five for three dropping to three or four paperbacks steps down to three for two, and three for two
       raised to five by the quantity buttons steps up to five for three. */
    Object.values(info || {}).forEach((deal) => {
      const have = count(deal.product);
      const inCart = codes.filter((c) => deal.tiers[c.toUpperCase()]);
      if (!inCart.length) return;
      let best = null;
      Object.entries(deal.tiers).forEach(([c, n]) => { if (n <= have && (!best || n > best.n)) best = { c, n }; });
      if (!best) {
        drop.push(...inCart);
        clear[deal.from] = '';
      } else if (inCart.length !== 1 || !same(inCart[0], best.c)) {
        drop.push(...inCart);
        add.push(best.c);
      }
    });
    const needCount = needs ? String(needs.product || '').split(',').filter(Boolean).reduce((n, id) => n + count(id), 0) : 0;
    if (needs && needs.code && codes.some((c) => same(c, needs.code)) && needCount < 1) drop.push(needs.code);
    if (!drop.length) return;
    const sections = sectionIds();
    const others = codes.filter((c) => !drop.some((d) => same(c, d)));
    others.push(...add);
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: deferred.promise }));
    const result = await json(routes.cart_update_url + '.js', { updates: {}, ...(Object.keys(clear).length ? { attributes: clear } : {}), discount: others.join(','), sections });
    const fresh = result.items && result.token ? result : await getCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(fresh),
      detail: { items: fresh.items, source: 'lq-cart-bogo', sections: result.sections, didError: false },
    });
  } catch (e) {
    /* leave the cart as it is; checkout still rejects the code below three paperbacks */
  } finally {
    bogoChecking = false;
  }
}
readBogo();
readNeeds();
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  readBogo();
  readNeeds();
  const p = event.promise;
  if (!p || !p.then) return;
  p.then((res) => {
    const src = res && res.detail && res.detail.source;
    if (src === 'lq-cart-bogo') return;
    setTimeout(enforceBogo, 50);
  }).catch(() => {});
});

/* The paperback deals' deadline: starts the first time a visitor sees the card, never restarts. */
class LqCartOffer extends HTMLElement {
  static get observedAttributes() { return ['data-on']; }
  /* The drawer morphs this element in place, so a cart that drops the code flips data-on without reconnecting. */
  attributeChangedCallback(name, was, now) {
    if (was !== now && this.isConnected) { clearInterval(this.timer); this.connectedCallback(); }
  }
  connectedCallback() {
    if (this.dataset.on === 'true') return;
    /* h:mm:ss, e.g. 2:43:27 */
    const parts = String(this.dataset.time || '').split(':').map((n) => parseInt(n, 10) || 0);
    while (parts.length < 3) parts.unshift(0);
    const ms = ((parts[0] * 3600) + (parts[1] * 60) + parts[2]) * 1000 || 9807000;
    let end = parseInt(store.get(BOGO_END), 10);
    if (!end) { end = Date.now() + ms; store.set(BOGO_END, String(end)); }
    /* Ended is kept on <html>, which the drawer never re-renders, so a re-drawn card cannot come back empty. */
    if (end <= Date.now()) { document.documentElement.classList.add('lq-deal-ended'); return; }
    const pad = (n) => String(n).padStart(2, '0');
    const tick = () => {
      if (this.dataset.on === 'true') { clearInterval(this.timer); return; }
      const left = end - Date.now();
      if (left <= 0) { clearInterval(this.timer); document.documentElement.classList.add('lq-deal-ended'); return; }
      const s = Math.ceil(left / 1000);
      const out = this.querySelector('[data-lq-clock]');
      if (out) out.textContent = pad(Math.floor(s / 3600)) + ':' + pad(Math.floor((s % 3600) / 60)) + ':' + pad(s % 60);
    };
    tick();
    this.timer = setInterval(tick, 1000);
  }
  disconnectedCallback() {
    clearInterval(this.timer);
  }
}
if (!customElements.get('lq-cart-offer')) customElements.define('lq-cart-offer', LqCartOffer);
